import { TFilter, TPageableDataWithContentUi } from 'data/types/Table.types';
import {
  TPropertiesSearchListUI,
  TPropertyManagementSearchListDB,
  TPropertyManagementSearchListUI,
  TPropertySearchDB,
  TUnitDB,
  TUnitUI,
} from 'data/types/PropertyManagement.types';
import {
  getPropertiesSearchListConvertor,
  getPropertyManagementSearchListConvertor,
  getUnitsCoverter,
} from 'data/convertors/PropertyManagement.convertors';
import { EPropertyManagementUrls } from 'data/urls/propertyManagement.url';
import { authSplitApi } from '../helpers/slice.helpers';
import { TOptionsType } from 'components/Base/Select/Select.types';
import { TOptionTypeUi } from 'data/types/GeneralTypes';
import { selectOptionConverter } from 'data/convertors/General.converters';
import { analyticsLandlordsQueryConverter } from 'data/convertors/Landlord.converters';

/**
 * Page size for the property search. The endpoint is platform-wide (~37k rows) and applies no
 * ordering, so a response that fills this page is a truncated slice rather than a complete
 * answer - callers that show it to a user are expected to say so (PP-10035).
 */
export const PROPERTY_SEARCH_PAGE_SIZE = 2000;

export const PropertyManagementApi = authSplitApi('propertyManagement', [
  'propertyManagementSearchList',
  'propertiesSearchList',
]).injectEndpoints({
  endpoints: build => ({
    getPropertyManagementSearchList: build.query<
      TPropertyManagementSearchListUI[],
      TFilter
    >({
      query({ search }) {
        return {
          url: `${EPropertyManagementUrls.PROPERTY_MANAGEMENT}/${EPropertyManagementUrls.SEARCH}`,
          method: 'GET',
          params: {
            size: 2000,
            ...(search && { search }),
          },
        };
      },
      providesTags: ['propertyManagementSearchList'],
      transformResponse: (
        data: TPageableDataWithContentUi<TPropertyManagementSearchListDB[]>,
      ) => {
        const { content } = data;
        return getPropertyManagementSearchListConvertor.fromDb(content);
      },
    }),
    getPropertiesList: build.query<
      TPropertiesSearchListUI[],
      {
        search?: string;
        propertyManagementId?: string;
        allPropertyManagements?: boolean;
      }
    >({
      query({ search, propertyManagementId, allPropertyManagements }) {
        return {
          url: `${EPropertyManagementUrls.PROPERTIES}/${EPropertyManagementUrls.SEARCH}`,
          method: 'GET',
          // An unscoped search returns every customer's properties, so the
          // property-management filter must never depend on another parameter
          // also being present. The backend now rejects a search that carries
          // neither a scope nor an explicit `allPropertyManagements`, so a
          // caller that forgets the scope gets an error rather than everyone.
          params: {
            size: PROPERTY_SEARCH_PAGE_SIZE,
            ...(propertyManagementId && {
              propertyManagementIds: [propertyManagementId],
            }),
            ...(search && { nameSearch: search }),
            ...(allPropertyManagements && { allPropertyManagements: true }),
          },
        };
      },
      providesTags: ['propertiesSearchList'],
      transformResponse: (
        data: TPageableDataWithContentUi<TPropertySearchDB[]>,
      ) => {
        const { content } = data;
        return getPropertiesSearchListConvertor.fromDb(content);
      },
    }),
    getUnits: build.query<TUnitUI[], { propertyId: string }>({
      query(params) {
        return {
          url: `${EPropertyManagementUrls.PROPERTIES}/${EPropertyManagementUrls.UNITS}`,
          method: 'GET',
          params,
        };
      },
      transformResponse: (data: TUnitDB[]) =>
        data?.map(item => getUnitsCoverter.fromDb(item)) || [],
      providesTags: ['units'],
    }),
    getPropertyManagements: build.query<
      TOptionsType,
      { statuses: TOptionTypeUi[] }
    >({
      query({ statuses }) {
        return {
          url: `${EPropertyManagementUrls.PROPERTY_MANAGEMENT}/${EPropertyManagementUrls.ALL}`,
          method: 'GET',
          params: statuses.length
            ? analyticsLandlordsQueryConverter.toDb(statuses)
            : undefined,
        };
      },
      transformResponse: (data: TOptionsType) =>
        (data || []).map(pm => selectOptionConverter.fromDb(pm)),
    }),
  }),
  overrideExisting: false,
});

export const {
  useGetPropertyManagementSearchListQuery,
  useGetPropertiesListQuery,
  useGetUnitsQuery,
  useGetPropertyManagementsQuery,
} = PropertyManagementApi;
